import { DRIVEN_ROW, answerForDrivenRow, answerSourceKey, drivenRowIdFor, drivenRowsFor, driverRefusal, sourceOptionKey } from './driven-rows';
import { normalizeKeyword } from './keyword';
const ALIAS_ROW_PREFIX = 'ur_';
/**
 * WHAT MAKES TWO OPTIONS THE SAME OPTION, and the one place it is decided.
 *
 * `normalizeKeyword` with both sensitivities off, which is the matcher the graders already
 * use: "Ocean" on one site and "ocean" on another are the same answer to a teacher, and a
 * second implementation of "same words" is how a merge and a grader come to disagree.
 *
 * `other` and `blank` key on themselves — there is exactly one of each in a union however
 * many members contribute one.
 */
export function aliasRowKey(row) {
    if (row.kind !== 'option')
        return row.id;
    const assetId = row.media?.assetId;
    return typeof assetId === 'string' && assetId
        ? `asset:${assetId}`
        : `text:${normalizeKeyword(row.label, false, false)}`;
}
/** The union row's own id. Stable for as long as the key is — see the note on renaming. */
export const aliasRowId = (key) => `${ALIAS_ROW_PREFIX}${key}`;
export const isAliasRowId = (id) => typeof id === 'string' && id.startsWith(ALIAS_ROW_PREFIX);
/** The variable chips an option's authored html names. */
function chipNamesIn(html) {
    if (!html)
        return [];
    const out = [];
    const re = /data-variable-chip="([^"]*)"/g;
    let m;
    while ((m = re.exec(html)) !== null)
        if (m[1])
            out.push(m[1]);
    return out;
}
function memberBlock(member, tasks) {
    return tasks.find(tk => tk.id === member.taskId)?.blocks?.find(b => b.id === member.blockId);
}
/**
 * THE MERGED ANSWER SPACE, and every reason it might not exist.
 *
 * `experienceVariableNames` is REQUIRED rather than optional: a chip's scope is the whole of
 * the `taskScoped` refusal, and a default would silently admit exactly the members the rule
 * exists to keep out. The caller holds both scopes already.
 *
 * FAULTS ARE TOTAL. One bad member makes the union wrong rather than smaller — a missing
 * block's options are simply absent from the space, so the students on that route land on
 * Blank with nothing saying why. Rows go null and the faults say which blocks to fix.
 */
export function aliasDriverSpace(alias, tasks, experienceVariableNames) {
    const faults = [];
    /* Insertion order IS the row order, so the union reads in member order and, within a
       member, in the teacher's own option order. A Map keeps that for free. */
    const merged = new Map();
    /* WHICH MEMBER OPTIONS EACH CLASS IS MADE OF — see `DrivenRow.sourceOptions`. Accumulated
       across members rather than taken from the first, because a rename of ANY contributor has
       to be recognisable, not just a rename of whichever member happened to be listed first. */
    const sources = new Map();
    let sawOther = false;
    for (const member of alias.members) {
        const block = memberBlock(member, tasks);
        if (!block) {
            faults.push({ kind: 'missing', member });
            continue;
        }
        const data = (block.data ?? {});
        const refusal = driverRefusal(block.type, data, member.facet);
        if (refusal) {
            faults.push({ kind: 'refused', member, refusal });
            continue;
        }
        const rows = drivenRowsFor(block.type, data, member.facet);
        /* `driverRefusal` already answered this, so a null here would be the two disagreeing.
           Treated as a refusal rather than skipped, because a silent skip is a shrunk union. */
        if (!rows) {
            faults.push({ kind: 'refused', member, refusal: 'noOptions' });
            continue;
        }
        let scoped;
        for (const row of rows) {
            const name = chipNamesIn(row.html).find(n => !experienceVariableNames.has(n));
            if (name !== undefined) {
                scoped = name;
                break;
            }
        }
        if (scoped !== undefined) {
            faults.push({ kind: 'taskScoped', member, variable: scoped });
            continue;
        }
        for (const row of rows) {
            if (row.kind === 'blank')
                continue;
            if (row.kind === 'other') {
                sawOther = true;
                continue;
            }
            const key = aliasRowKey(row);
            const from = sourceOptionKey(answerSourceKey(member.taskId, member.blockId, member.facet), row.id);
            const seen = sources.get(key);
            if (seen) {
                if (!seen.includes(from))
                    seen.push(from);
            }
            else
                sources.set(key, [from]);
            /* FIRST MEMBER WINS THE PRESENTATION, not the identity. The key decides what is the
               same row; the html and media are only how it is shown, and the first member to
               contribute the key is as good a source as any — they are equivalents by definition.
               The STAND-IN comes from that same member, and must: an answer only selects the row on
               the block it was authored on, so a sample and its presentation have to agree about
               which member they came from. See `DrivenRow.sample`. */
            if (!merged.has(key)) {
                merged.set(key, {
                    ...row,
                    id: aliasRowId(key),
                    sample: answerForDrivenRow(block.type, row, member.facet) ?? undefined,
                });
            }
        }
    }
    if (faults.length)
        return { rows: null, faults };
    if (!merged.size)
        return { rows: null, faults };
    const rows = [...merged.entries()]
        .map(([key, row]) => ({ ...row, sourceOptions: sources.get(key) }));
    /* The catch-all where ANY member is open, because a student on that route can type
       something no option names. Blank always, for the routes that reached no member at all —
       which on an alias is not an edge case but the whole point of a fallback row. */
    if (sawOther)
        rows.push({ id: DRIVEN_ROW.other, label: '', kind: 'other' });
    rows.push({ id: DRIVEN_ROW.blank, label: '', kind: 'blank' });
    return { rows, faults };
}
/**
 * What the teacher is signing up for, said BEFORE the link is made.
 *
 * Reported as the real cost of the feature: nine rows appearing after the fact is the
 * surprise this exists to prevent. Null when the alias cannot drive at all.
 */
export function aliasDriverVersionCount(alias, tasks, experienceVariableNames) {
    return aliasDriverSpace(alias, tasks, experienceVariableNames).rows?.length ?? null;
}
/**
 * WHICH UNION ROW THE STUDENT'S OWN ANSWER LANDS IN.
 *
 * The alias half of `drivenRowIdFor`, and it delegates to it rather than re-deriving: the
 * member's own block decides which of ITS rows the answer is, by ids and by the grader's own
 * matcher, and this only translates that row into the union's key. A second matcher here
 * would disagree with the feedback the same answer fires.
 *
 * MEMBERS ARE EQUIVALENTS, so at most one can have been answered and order decides nothing —
 * `aliasMembersExclusive` is what guarantees that, and is enforced when the alias is formed.
 * Taking the first answered member is therefore not a precedence rule; it is the only one.
 */
export function aliasDrivenRowId(alias, tasks, answerAt, experienceVariableNames) {
    const { rows } = aliasDriverSpace(alias, tasks, experienceVariableNames);
    if (!rows)
        return null;
    /**
     * RECOGNISING BEATS CATCHING, which is why this walks every member before settling.
     *
     * It used to return `other` from the first member that caught the answer. At RUNTIME that is
     * identical — members are equivalents, so exactly one has answered and the loop sees one
     * member. It is the editor's STAND-IN that is offered to every member at once, and there an
     * open space swallows everything: the owner's first pathway asks a keyword question, whose
     * catch-all matches any text, so "Squirrel" — a real option on the SECOND pathway — landed on
     * pathway one's catch-all and never selected its own row. Five offered answers, three
     * destinations, and the version pager stepped two and wrapped.
     *
     * So a member that places the answer as one of its OWN options wins over one that merely
     * catches it, and `other` is the answer only when nobody recognised it.
     */
    let caughtAsOther = false;
    for (const member of alias.members) {
        const answer = answerAt(member.taskId, member.blockId);
        if (answer === undefined || answer === null)
            continue;
        const block = memberBlock(member, tasks);
        if (!block)
            continue;
        const data = (block.data ?? {});
        const rowId = drivenRowIdFor(block.type, data, answer, member.facet);
        if (rowId === null)
            continue;
        if (rowId === DRIVEN_ROW.blank)
            continue;
        if (rowId === DRIVEN_ROW.other) {
            caughtAsOther = true;
            continue;
        }
        const own = drivenRowsFor(block.type, data, member.facet)?.find(r => r.id === rowId);
        if (!own)
            continue;
        const id = aliasRowId(aliasRowKey(own));
        /* The union must actually hold it. It always should — the row came from a member — and
           returning an id with no version would strand the student on `pickVersion`'s fallback,
           which is the one failure this whole design exists to avoid. */
        return rows.some(r => r.id === id) ? id : null;
    }
    if (caughtAsOther)
        return DRIVEN_ROW.other;
    /* Nobody's route reached a member. This is the row that makes an alias link safe to offer
       at all, which is why the union always has one. */
    return DRIVEN_ROW.blank;
}
//# sourceMappingURL=alias-driver.js.map